import { AcademyGuideSummaryText } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/components/GuideCards/AcademyGuideSummaryText";
import { ChaptersReadDisplay } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/components/GuideCards/ChaptersReadDisplay";
import {
  AnimatedUnderlineText,
  UnderlineHoverStyle,
  ZoomHoverStyle,
  ZoomImage,
  ZoomImageContainer,
} from "@faire/retailer-visitor-shared/views/open-with-faire/academy/components/GuideCards/HoverStyles";
import type { FaireAcademyGuide } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/types";
import { optimizeImageUrlFillRectangle } from "@faire/web--source/common/images/optimizeImages";
import { ITraceEventMarker } from "@faire/web--source/common/pete/TraceEventMarkerTracker";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import * as React from "react";
import styled from "styled-components";

const LARGE_CARD_IMG_HEIGHT = 283;
const LARGE_CARD_IMG_WIDTH = 423;

const BACKGROUND_IMG_HEIGHT = 347;
const BACKGROUND_IMG_WIDTH = 1053;
const BACKGROUND_IMAGE =
  "https://cdn.faire.com/fastly/2091a2c409b9f805cac4f29329f4bfb41980c577628641ff884c8a675c5e3ba4.jpeg";

export const AcademyGuideCardLarge: React.FC<{
  guide: FaireAcademyGuide;
  onClick?: () => ITraceEventMarker;
  className?: string;
  chaptersRead?: number;
  totalChapters?: number;
}> = ({ guide, onClick, className, chaptersRead, totalChapters }) => {
  const isSSR = useIsSSR();
  const {
    title,
    cardSummary,
    featuredImage,
    readTimeMinutes,
    chapters,
    linkToPage,
    likes,
  } = guide;
  const backgroundImageUrl = optimizeImageUrlFillRectangle(
    IImage.build({ url: BACKGROUND_IMAGE }),
    isSSR,
    BACKGROUND_IMG_WIDTH,
    BACKGROUND_IMG_HEIGHT
  );

  return (
    <Link to={linkToPage} onClick={onClick} className={className}>
      <PaddedRow gap="6x" $backgroundImage={backgroundImageUrl}>
        <ZoomImageContainer
          $height={LARGE_CARD_IMG_HEIGHT}
          $width={LARGE_CARD_IMG_WIDTH}
        >
          <ZoomImage
            src={optimizeImageUrlFillRectangle(
              IImage.build({ url: featuredImage }),
              isSSR,
              LARGE_CARD_IMG_WIDTH * 2,
              LARGE_CARD_IMG_HEIGHT * 2
            )}
            height={LARGE_CARD_IMG_HEIGHT}
            width={LARGE_CARD_IMG_WIDTH}
            alt=""
          />
        </ZoomImageContainer>
        <TextColumnWrapper justify="center">
          <Typography variant="displaySSerifRegular">
            <AnimatedUnderlineText>{title}</AnimatedUnderlineText>
          </Typography>
          <Spacer height="1x" />
          <Typography>{cardSummary}</Typography>
          <Spacer height="2x" />
          {chaptersRead && totalChapters ? (
            <ChaptersReadDisplay
              chaptersRead={chaptersRead}
              totalChapters={totalChapters}
            />
          ) : (
            <AcademyGuideSummaryText
              numChapters={chapters.length}
              readTimeMinutes={readTimeMinutes}
              likes={likes}
            />
          )}
        </TextColumnWrapper>
      </PaddedRow>
    </Link>
  );
};

const PaddedRow = styled(Row)<{ $backgroundImage: string | undefined }>`
  background-image: url("${({ $backgroundImage }) => $backgroundImage}");
  background-size: cover;
  padding: ${getSpacing("4x", 88, "4x", "4x")};
  height: 347px;
  width: 100%;

  &:hover {
    cursor: pointer;

    ${UnderlineHoverStyle}

    ${ZoomHoverStyle}
  }
`;

const TextColumnWrapper = styled(Column)`
  max-width: 342px;
`;
